/**
 * Lieferantenseite und Material am Projekt (Migration 172, 25.09.2026).
 * Server: kontor_material.py → lieferant_stamm.py, projekt_material_store.py.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from "@vimaana/ui";

// ── Typen ────────────────────────────────────────────────────────────────

export type LieferantZeile = {
  id: string; name: string; adresse: string;
  /** Warengruppen (Migration 218), mehrere möglich; leer = keine, dann ggf. ein Vorschlag. */
  gruppen: string[]; gruppe_vorschlag: string;
  kontakte: number; materialien: number; standard: number; projekte_laufend: number;
  telefon: string; email: string;
};

export type LieferantKontakt = { id: string; name: string; email: string; telefon: string; rolle: string; notizen?: string };

/** Übergeordnete Art im Stamm (Migration 173). */
export type MaterialArt = "material" | "maschine";

export type LieferantMaterial = {
  material_id: string; name: string; einheit: string; kategorie: string; art: MaterialArt;
  artikelnummer: string; preis_netto: number | null; notiz: string;
  standard: boolean; standard_bei: string;
  /** Migration 182: Belegdatum des Preises und der letzte andere Preis davor. */
  preis_am?: string | null; preis_vorher?: number | null; preis_vorher_am?: string | null;
};

export type LieferantProjekt = { id: string; title: string; status: string; kunde_name: string; laufend: boolean; materialien: number };

/** Stand einer Lieferantenrechnung (lieferant_stamm.RECHNUNG_STAND_SQL). */
export type RechnungStand = "erfassung" | "offen" | "im_korb" | "angewiesen" | "bezahlt";
export type LieferantRechnung = {
  id: string; datum: string; rechnungsnummer: string; brutto: number; faelligkeit: string;
  beschreibung: string; projekt_title: string; projekt_id: string; beleg_status: string;
  stand: RechnungStand; bezahlt_am: string; ueberfaellig: boolean;
};

export type LieferantBlattDaten = {
  id: string; name: string; adresse: string; notizen: string; gruppen: string[]; gruppe_vorschlag: string;
  /** Unsere Kundennummer beim Lieferanten (Migration 175). */
  kundennummer: string;
  kontakte: LieferantKontakt[]; materialien: LieferantMaterial[]; projekte: LieferantProjekt[];
  rechnungen: { liste: LieferantRechnung[]; offen_anzahl: number; offen_summe: number; anzahl: number };
};

export type MaterialStamm = { id: string; name: string; einheit: string; kategorie: string; art: MaterialArt; standard_id: string; standard_name: string };

/** Wem eine Anfrage gilt (material_anfrage.anfrage_kontakt): guete "" = keine brauchbare Adresse. */
export type AnfrageEmpfang = { email: string; name: string; guete: "anfrage" | "allgemein" | "rechnung" | ""; hinweis: string };

/** Wer ein Material führt — für den Preisvergleich. */
export type MaterialAnbieter = {
  id: string; name: string; preis_netto: number | null; artikelnummer: string; standard: boolean;
  anfrage_an: AnfrageEmpfang | null;
};

export type AnfrageStatus = "angefragt" | "angebot" | "gewaehlt" | "nicht_gewaehlt" | "abgesagt";
export type BestellStatus = "" | "bestellt" | "geliefert";

/** Ein Material in einer Anfrage (Migration 175). */
export type MaterialAnfrageStand = {
  anfrage_id: string; lieferant_id: string; lieferant_name: string; gesendet_am: string;
  status: AnfrageStatus; preis_netto: number | null; lieferzeit: string; notiz: string;
  menge: string; einheit: string; antwort: boolean;
};

/** Eine gesendete Anfrage an einen Lieferanten. */
export type AnfrageKopf = {
  id: string; lieferant_id: string; lieferant_name: string; an: string; gesendet_am: string; gesendet_von: string;
  wiedervorlage_am: string; nachgefasst_am: string; erledigt: boolean; message_id: string;
  antwort: { id: string; ts: string } | null; offen: boolean; faellig: boolean; pm_ids: string[];
  /** Etappe 8: was der Evo aus der Antwort gelesen hat — Vorschlag zum Bestätigen. */
  gelesen?: AntwortGelesen | null;
};

export type AntwortGelesen = {
  auftrag_id: string;
  status: "liest" | "fertig" | "fehler";
  positionen?: { pm_id: string; preis_netto: number | null; einheit: string; lieferzeit: string; absage: boolean; notiz: string }[];
  angebot_im_anhang?: boolean;
};

export type ProjektMaterialZeile = {
  id: string; material_id: string; name: string; menge: string; einheit: string; notiz: string; art: MaterialArt;
  lieferant_id: string; lieferant_name: string; standard_id: string; standard_name: string;
  abweichend: boolean; artikelnummer: string; angebot_id: string; position_key: string;
  bestell_status: BestellStatus;
  /** Laut Lieferschein: jüngstes Lieferdatum und Menge je Einheit („17,4 t"); leer = nichts geliefert. */
  geliefert_am: string; geliefert: string;
  anbieter: MaterialAnbieter[]; anfragen: MaterialAnfrageStand[];
};

export type ProjektLieferantGruppe = {
  lieferant_id: string; name: string; adresse: string;
  kontakte: LieferantKontakt[]; material_ids: string[]; von_hand: boolean;
  /** Kam über eine Rechnung oder einen Beleg ins Projekt (projekt_lieferanten_alle). */
  aus_beleg?: boolean;
  anfrage_an: AnfrageEmpfang | null;
};

export type ProjektMaterialDaten = {
  projekt: { id: string; title: string; ort: string };
  materialien: ProjektMaterialZeile[];
  gruppen: ProjektLieferantGruppe[];
  ohne_lieferant: string[];
  angebot_offen: number;
  anfragen: AnfrageKopf[];
  /** VG-xxxxxx — steht in jeder Anfrage, ScanFlow ordnet Belege daran zu. */
  kommission: string;
};

export type AnfragePruefung = { stufe: "fehler" | "hinweis" | "info"; text: string; aktion: string };
export type AnfrageEntwurf = {
  lieferant_id: string; lieferant_name: string; an: string; an_name: string; an_guete: AnfrageEmpfang["guete"];
  ziele: { email: string; wer: string }[]; betreff: string; text: string; pruefung: AnfragePruefung[];
  kommission: string; pm_ids: string[];
};

/** Die Rolle, die einen Ansprechpartner zum Empfänger von Anfragen macht (material_anfrage.ANFRAGE_ROLLE). */
export const ANFRAGE_ROLLE = "Anfragen & Bestellung";
export const istAnfrageRolle = (rolle: string) => /anfrage|bestell|verkauf|vertrieb|au(ß|ss)endienst|innendienst|dispo/i.test(rolle || "");

export type AngebotPosition = {
  key: string; titel: string; text: string; menge: string; einheit: string; optional: boolean;
  vorschlag: { id: string; name: string; einheit: string; art: MaterialArt } | null;
  uebernommen: boolean; schon_in_liste: boolean;
};

export type AngebotMitPositionen = { id: string; art: "angebot" | "entwurf"; nummer: string; status: string; titel: string; positionen: AngebotPosition[] };

/** Eine Zeile der Materialansicht (`GET /kontor/material/uebersicht`). */
export type MaterialUebersichtZeile = {
  id: string; name: string; einheit: string; kategorie: string; art: MaterialArt;
  standard_id: string; standard_name: string; lieferanten: number; projekte_laufend: number;
};

export type MaterialLieferant = {
  id: string; name: string; adresse: string; gruppen: string[]; artikelnummer: string;
  preis_netto: number | null; notiz: string; standard: boolean; telefon: string;
};

export type MaterialBlattDaten = {
  id: string; name: string; einheit: string; kategorie: string; art: MaterialArt; beschreibung: string; standard_id: string;
  lieferanten: MaterialLieferant[];
  projekte: { id: string; title: string; status: string; laufend: boolean }[];
};

export type UebernahmeEintrag = {
  material_id?: string; neu_name?: string; kategorie?: string;
  menge?: string; einheit?: string; notiz?: string; angebot_id?: string; position_key?: string;
};

const enc = encodeURIComponent;

// ── Lieferantenseite ─────────────────────────────────────────────────────

export function useLieferantenUebersicht(enabled = true) {
  return useQuery({
    queryKey: ["lieferanten", "uebersicht"],
    queryFn: () => apiGet<{ lieferanten: LieferantZeile[] }>("/api/kontor/lieferanten/uebersicht"),
    enabled,
    staleTime: 30_000,
  });
}

export function useLieferantBlatt(id: string) {
  return useQuery({
    queryKey: ["lieferant", id],
    queryFn: () => apiGet<LieferantBlattDaten>(`/api/kontor/lieferanten/${enc(id)}`),
    enabled: !!id,
  });
}

/** Nach jeder Änderung am Lieferanten: Blatt, Übersicht, Projektlisten, Termin-Orte neu. */
function useNeuLaden() {
  const qc = useQueryClient();
  return (blatt?: LieferantBlattDaten) => {
    if (blatt) qc.setQueryData(["lieferant", blatt.id], blatt);
    qc.invalidateQueries({ queryKey: ["lieferant"] });
    qc.invalidateQueries({ queryKey: ["lieferanten"] });
    qc.invalidateQueries({ queryKey: ["projekt-material"] });
    qc.invalidateQueries({ queryKey: ["termin-orte"] });
    qc.invalidateQueries({ queryKey: ["material"] });
    qc.invalidateQueries({ queryKey: ["anfrage-entwurf"] });
  };
}

export function useLieferantAendern(id: string) {
  const neu = useNeuLaden();
  return useMutation({
    mutationFn: (b: { name?: string; adresse?: string; notizen?: string; gruppen?: string[]; kundennummer?: string }) =>
      apiPatch<LieferantBlattDaten>(`/api/kontor/lieferanten/${enc(id)}`, b),
    onSuccess: (d) => neu(d),
  });
}

export function useLieferantKontakt(id: string) {
  const neu = useNeuLaden();
  const anlegen = useMutation({
    mutationFn: (b: Partial<LieferantKontakt>) => apiPost<LieferantKontakt>(`/api/kontor/lieferanten/${enc(id)}/kontakte`, b),
    onSuccess: () => neu(),
  });
  const aendern = useMutation({
    mutationFn: (v: { kontaktId: string; werte: Partial<LieferantKontakt> }) =>
      apiPatch<LieferantKontakt>(`/api/kontor/lieferanten/kontakte/${enc(v.kontaktId)}`, v.werte),
    onSuccess: () => neu(),
  });
  const entfernen = useMutation({
    mutationFn: (kontaktId: string) => apiDelete(`/api/kontor/lieferanten/kontakte/${enc(kontaktId)}`),
    onSuccess: () => neu(),
  });
  return { anlegen, aendern, entfernen };
}

export function useLieferantMaterial(id: string) {
  const neu = useNeuLaden();
  const setzen = useMutation({
    mutationFn: (v: { materialId: string; artikelnummer?: string; preis_netto?: number | null; notiz?: string }) =>
      apiPut<LieferantBlattDaten>(`/api/kontor/lieferanten/${enc(id)}/material/${enc(v.materialId)}`, {
        artikelnummer: v.artikelnummer ?? "", preis_netto: v.preis_netto ?? null, notiz: v.notiz ?? "",
      }),
    onSuccess: (d) => neu(d),
  });
  const entfernen = useMutation({
    mutationFn: (materialId: string) => apiDelete<LieferantBlattDaten>(`/api/kontor/lieferanten/${enc(id)}/material/${enc(materialId)}`),
    onSuccess: (d) => neu(d as LieferantBlattDaten),
  });
  const standard = useMutation({
    mutationFn: (v: { materialId: string; standard: boolean }) =>
      apiPost<LieferantBlattDaten>(`/api/kontor/lieferanten/${enc(id)}/material/${enc(v.materialId)}/standard`, { standard: v.standard }),
    onSuccess: (d) => neu(d),
  });
  return { setzen, entfernen, standard };
}

// ── Materialstamm ────────────────────────────────────────────────────────

export function useMaterialSuche(q: string, enabled = true) {
  return useQuery({
    queryKey: ["material", "suche", q.trim()],
    queryFn: () => apiGet<{ materialien: MaterialStamm[] }>(`/api/kontor/material?q=${enc(q.trim())}&grenze=30`),
    enabled,
    staleTime: 30_000,
  });
}

export function useMaterialStammlisten() {
  return useQuery({
    queryKey: ["material", "stammlisten"],
    queryFn: () => apiGet<{ gruppen: { slug: string; label: string }[]; arten: { slug: MaterialArt; label: string }[]; kategorien: { slug: string; label: string }[]; einheiten: string[] }>("/api/kontor/material/stammlisten"),
    staleTime: 10 * 60_000,
  });
}

/** Neues Material im Stamm — dieselbe Namensgleich-Prüfung wie ScanFlow (kein zweites „Humus"). */
export function useMaterialAnlegen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: { name: string; einheit?: string; kategorie?: string; art?: MaterialArt }) =>
      apiPost<{ id: string; name: string; einheit: string; kategorie: string; art: MaterialArt; neu: boolean }>("/api/kontor/material", b),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["material"] }),
  });
}

export function useMaterialLieferanten(materialId: string, enabled = true) {
  return useQuery({
    queryKey: ["material", "lieferanten", materialId],
    queryFn: () => apiGet<{ lieferanten: { id: string; name: string; artikelnummer: string; preis_netto: number | null; standard: boolean }[] }>(
      `/api/kontor/material/${enc(materialId)}/lieferanten`),
    enabled: enabled && !!materialId,
  });
}

/** Vorgeschlagene Warengruppen festschreiben (ohne ids: alle mit Vorschlag). */
export function useGruppenVorschlaege() {
  const neu = useNeuLaden();
  return useMutation({
    mutationFn: (ids?: string[]) => apiPost<{ gesetzt: number }>("/api/kontor/lieferanten/gruppen/vorschlaege", { ids: ids ?? null }),
    onSuccess: () => neu(),
  });
}

export function useMaterialUebersicht(enabled = true) {
  return useQuery({
    queryKey: ["material", "uebersicht"],
    queryFn: () => apiGet<{ materialien: MaterialUebersichtZeile[] }>("/api/kontor/material/uebersicht"),
    enabled,
    staleTime: 30_000,
  });
}

export function useMaterialBlatt(id: string) {
  return useQuery({
    queryKey: ["material", "blatt", id],
    queryFn: () => apiGet<MaterialBlattDaten>(`/api/kontor/material/${enc(id)}`),
    enabled: !!id,
  });
}

export function useMaterialAendern(id: string) {
  const neu = useNeuLaden();
  return useMutation({
    mutationFn: (b: { name?: string; einheit?: string; kategorie?: string; art?: MaterialArt }) =>
      apiPatch<MaterialBlattDaten>(`/api/kontor/material/${enc(id)}`, b),
    onSuccess: () => neu(),
  });
}

/**
 * Lieferant ↔ Material aus beliebiger Richtung — die Materialansicht kennt den
 * Lieferanten erst beim Klick. Dieselben Routen wie `useLieferantMaterial`.
 */
export function useZuordnung() {
  const neu = useNeuLaden();
  const setzen = useMutation({
    mutationFn: (v: { firmaId: string; materialId: string; artikelnummer?: string; preis_netto?: number | null; notiz?: string }) =>
      apiPut<LieferantBlattDaten>(`/api/kontor/lieferanten/${enc(v.firmaId)}/material/${enc(v.materialId)}`, {
        artikelnummer: v.artikelnummer ?? "", preis_netto: v.preis_netto ?? null, notiz: v.notiz ?? "",
      }),
    onSuccess: () => neu(),
  });
  const entfernen = useMutation({
    mutationFn: (v: { firmaId: string; materialId: string }) =>
      apiDelete<LieferantBlattDaten>(`/api/kontor/lieferanten/${enc(v.firmaId)}/material/${enc(v.materialId)}`),
    onSuccess: () => neu(),
  });
  const standard = useMutation({
    mutationFn: (v: { firmaId: string; materialId: string; standard: boolean }) =>
      apiPost<LieferantBlattDaten>(`/api/kontor/lieferanten/${enc(v.firmaId)}/material/${enc(v.materialId)}/standard`, { standard: v.standard }),
    onSuccess: () => neu(),
  });
  return { setzen, entfernen, standard };
}

// ── Material am Projekt ──────────────────────────────────────────────────

export function useProjektMaterial(projektId: string) {
  return useQuery({
    queryKey: ["projekt-material", projektId],
    queryFn: () => apiGet<ProjektMaterialDaten>(`/api/kontor/projekt/${enc(projektId)}/material`),
    enabled: !!projektId,
  });
}

export function useAngebotPositionen(projektId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["projekt-material", projektId, "angebot"],
    queryFn: () => apiGet<{ angebote: AngebotMitPositionen[] }>(`/api/kontor/projekt/${enc(projektId)}/material/angebot`),
    enabled: enabled && !!projektId,
  });
}

export function useProjektMaterialAendern(projektId: string) {
  const qc = useQueryClient();
  const fertig = (d: ProjektMaterialDaten) => {
    qc.setQueryData(["projekt-material", projektId], d);
    qc.invalidateQueries({ queryKey: ["projekt-material", projektId, "angebot"] });
    qc.invalidateQueries({ queryKey: ["lieferant"] });
    qc.invalidateQueries({ queryKey: ["lieferanten"] });
    qc.invalidateQueries({ queryKey: ["termin-orte"] });
    qc.invalidateQueries({ queryKey: ["anfrage-entwurf"] });
  };
  const uebernehmen = useMutation({
    mutationFn: (eintraege: UebernahmeEintrag[]) =>
      apiPost<ProjektMaterialDaten & { neu: number }>(`/api/kontor/projekt/${enc(projektId)}/material`, { eintraege }),
    onSuccess: fertig,
  });
  const aendern = useMutation({
    mutationFn: (v: { id: string; menge?: string; einheit?: string; notiz?: string; lieferant_id?: string; als_standard?: boolean; bestell_status?: BestellStatus }) => {
      const { id, ...rest } = v;
      return apiPatch<ProjektMaterialDaten>(`/api/kontor/projekt/material/${enc(id)}`, rest);
    },
    onSuccess: fertig,
  });
  const entfernen = useMutation({
    mutationFn: (id: string) => apiDelete<ProjektMaterialDaten>(`/api/kontor/projekt/material/${enc(id)}`),
    onSuccess: (d) => fertig(d as ProjektMaterialDaten),
  });
  const lieferantDazu = useMutation({
    mutationFn: (firmaId: string) => apiPost<ProjektMaterialDaten>(`/api/kontor/projekt/${enc(projektId)}/lieferanten`, { lieferant_firma_id: firmaId }),
    onSuccess: fertig,
  });
  const lieferantWeg = useMutation({
    mutationFn: (firmaId: string) => apiDelete<ProjektMaterialDaten>(`/api/kontor/projekt/${enc(projektId)}/lieferanten/${enc(firmaId)}`),
    onSuccess: (d) => fertig(d as ProjektMaterialDaten),
  });
  return { uebernehmen, aendern, entfernen, lieferantDazu, lieferantWeg };
}

// ── Anfragen an Lieferanten (Migration 175, material_anfrage.py) ─────────

/** Die Anfrage-Mail an einen Lieferanten, fertig zum Prüfen. Nach einer Korrektur (Kontakt, Menge,
 *  Kundennummer) lädt sie neu — `useNeuLaden` und `useProjektMaterialAendern` räumen den Schlüssel ab. */
export function useAnfrageEntwurf(projektId: string, lieferantId: string, pmIds: string[], terminId = "") {
  return useQuery({
    queryKey: ["anfrage-entwurf", projektId, lieferantId, pmIds.join(","), terminId],
    queryFn: () => apiPost<AnfrageEntwurf>(`/api/kontor/projekt/${enc(projektId)}/anfrage/entwurf`, { lieferant_id: lieferantId, pm_ids: pmIds, ...(terminId ? { termin_id: terminId } : {}) }),
    enabled: !!projektId && !!lieferantId && pmIds.length > 0,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

/** Antworten eintragen, ein Angebot nehmen, eine Anfrage erledigen. */
export function useAnfrageStand(projektId: string) {
  const qc = useQueryClient();
  const fertig = (d: ProjektMaterialDaten) => {
    qc.setQueryData(["projekt-material", projektId], d);
    qc.invalidateQueries({ queryKey: ["lieferant"] });
    qc.invalidateQueries({ queryKey: ["material"] });
  };
  const position = useMutation({
    mutationFn: (v: { anfrageId: string; pmId: string; status?: AnfrageStatus; preis_netto?: number | null; preis_leeren?: boolean; lieferzeit?: string; notiz?: string }) => {
      const { anfrageId, pmId, ...rest } = v;
      return apiPatch<ProjektMaterialDaten>(`/api/kontor/anfrage/${enc(anfrageId)}/position/${enc(pmId)}`, rest);
    },
    onSuccess: fertig,
  });
  const waehlen = useMutation({
    mutationFn: (v: { anfrageId: string; pmId: string }) =>
      apiPost<ProjektMaterialDaten>(`/api/kontor/anfrage/${enc(v.anfrageId)}/position/${enc(v.pmId)}/waehlen`, {}),
    onSuccess: fertig,
  });
  const erledigt = useMutation({
    mutationFn: (v: { anfrageId: string; erledigt: boolean }) =>
      apiPost<ProjektMaterialDaten>(`/api/kontor/anfrage/${enc(v.anfrageId)}/erledigt`, { erledigt: v.erledigt }),
    onSuccess: fertig,
  });
  /* Etappe 8: Gelesenes übernehmen (Preise → Katalog des Lieferanten) oder verwerfen. */
  const gelesen = useMutation({
    mutationFn: (v: { anfrageId: string; auftragId: string; annehmen: boolean }) =>
      apiPost<ProjektMaterialDaten>(`/api/kontor/anfrage/${enc(v.anfrageId)}/gelesen`, { auftrag_id: v.auftragId, annehmen: v.annehmen }),
    onSuccess: fertig,
  });
  const lesen = useMutation({
    mutationFn: (v: { anfrageId: string; mailId: string }) =>
      apiPost<ProjektMaterialDaten>(`/api/kontor/anfrage/${enc(v.anfrageId)}/lesen`, { mail_id: v.mailId }),
    onSuccess: fertig,
  });
  return { position, waehlen, erledigt, gelesen, lesen };
}

/**
 * Neuen Lieferanten anlegen (POST /kontor/lieferanten, Dublettensperre wie
 * ScanFlow: exakt, sonst ähnlich). `force` legt trotz ähnlichem Namen an.
 * Die Antwort ist der vorhandene Lieferant, wenn es ihn schon gibt — der
 * Aufrufer vergleicht den Namen.
 */
export function useNeuerLieferant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { name: string; force?: boolean }) =>
      apiPost<{ id: string; name: string }>("/api/kontor/lieferanten", { name: v.name, force: !!v.force }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["lieferanten"] });
      qc.invalidateQueries({ queryKey: ["lieferant"] });
    },
  });
}
